import { forwardRef, ReactNode } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Badge,
  Paper,
  Stepper,
  Progress,
  Tooltip,
  ThemeIcon,
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconClock,
  IconAlertTriangle,
  IconCircle,
} from '@tabler/icons-react';
import { Card } from '../../ui';
import { FadeIn } from '../../animations';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface StatusStep {
  key: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  timestamp?: Date;
  actor?: string;
}

export interface StatusTrackerProps {
  steps: StatusStep[];
  currentStatus: string;
  title?: string;
  subtitle?: string;
  variant?: 'stepper' | 'progress' | 'timeline' | 'badges';
  showTimestamps?: boolean;
  orientation?: 'horizontal' | 'vertical';
  statusColors?: Record<string, string>;
  completedSteps?: string[];
  failedStep?: string;
  className?: string;
}

export const StatusTracker = forwardRef<HTMLDivElement, StatusTrackerProps>(
  (
    {
      steps,
      currentStatus,
      title,
      subtitle,
      variant = 'stepper',
      showTimestamps = true,
      orientation = 'horizontal',
      statusColors = {},
      completedSteps = [],
      failedStep,
      className,
    },
    ref
  ) => {
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const currentIndex = steps.findIndex((s) => s.key === currentStatus);
    const failedIndex = failedStep ? steps.findIndex((s) => s.key === failedStep) : -1;

    const getStepStatus = (index: number, stepKey: string) => {
      if (completedSteps.includes(stepKey)) return 'completed';
      if (failedStep === stepKey) return 'failed';
      if (index < currentIndex || completedSteps.includes(stepKey)) return 'completed';
      if (index === currentIndex) return 'current';
      return 'pending';
    };

    const getStatusColor = (status: string): string => {
      if (statusColors[status]) return statusColors[status];
      switch (status) {
        case 'completed':
          return 'green';
        case 'current':
          return 'blue';
        case 'failed':
          return 'red';
        default:
          return 'gray';
      }
    };

    const formatDate = (date?: Date) => {
      if (!date) return '';
      return new Date(date).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const renderStepper = () => {
      const activeStep = currentIndex >= 0 ? currentIndex : 0;

      return (
        <Stepper
          active={activeStep}
          orientation={orientation}
          size="sm"
          iconSize={32}
        >
          {steps.map((step, index) => {
            const status = getStepStatus(index, step.key);
            const color = getStatusColor(status);

            return (
              <Stepper.Step
                key={step.key}
                label={step.label}
                description={
                  showTimestamps && step.timestamp
                    ? formatDate(step.timestamp)
                    : step.description
                }
                icon={
                  status === 'completed' ? (
                    <IconCheck size={16} />
                  ) : status === 'failed' ? (
                    <IconX size={16} />
                  ) : (
                    step.icon || <IconCircle size={16} />
                  )
                }
                color={color}
                completedIcon={status === 'failed' ? <IconX size={16} /> : <IconCheck size={16} />}
              />
            );
          })}
        </Stepper>
      );
    };

    const renderProgress = () => {
      const progress = ((currentIndex + 1) / steps.length) * 100;
      const currentStep = steps[currentIndex];

      return (
        <Stack gap="sm">
          <Group justify="space-between">
            <Text size="sm" fw={500} style={accessibilityStyles}>
              {currentStep?.label || 'Unknown'}
            </Text>
            <Text size="sm" c="dimmed" style={accessibilityStyles}>
              Step {currentIndex + 1} of {steps.length}
            </Text>
          </Group>
          <Progress
            value={failedStep ? (failedIndex / steps.length) * 100 : progress}
            color={failedStep ? 'red' : 'blue'}
            size="lg"
            radius="xl"
          />
          <Group gap="xs">
            {steps.map((step, index) => {
              const status = getStepStatus(index, step.key);
              return (
                <Tooltip key={step.key} label={step.label}>
                  <Box
                    style={{
                      flex: 1,
                      height: 4,
                      borderRadius: 2,
                      backgroundColor: `var(--mantine-color-${getStatusColor(status)}-${
                        status === 'pending' ? '2' : '5'
                      })`,
                    }}
                  />
                </Tooltip>
              );
            })}
          </Group>
        </Stack>
      );
    };

    const renderTimeline = () => (
      <Stack gap="md">
        {steps.map((step, index) => {
          const status = getStepStatus(index, step.key);
          const color = getStatusColor(status);
          const isLast = index === steps.length - 1;

          return (
            <Group key={step.key} gap="md" align="flex-start">
              <Stack gap={0} align="center">
                <ThemeIcon
                  size={24}
                  radius="xl"
                  color={color}
                  variant={status === 'pending' ? 'light' : 'filled'}
                >
                  {status === 'completed' ? (
                    <IconCheck size={14} />
                  ) : status === 'failed' ? (
                    <IconX size={14} />
                  ) : status === 'current' ? (
                    <IconClock size={14} />
                  ) : (
                    <IconCircle size={14} />
                  )}
                </ThemeIcon>
                {!isLast && (
                  <Box
                    style={{
                      width: 2,
                      height: 40,
                      backgroundColor: `var(--mantine-color-${
                        status === 'completed' ? 'green' : 'gray'
                      }-3)`,
                    }}
                  />
                )}
              </Stack>
              <Box>
                <Text size="sm" fw={status === 'current' ? 600 : 400} style={accessibilityStyles}>
                  {step.label}
                </Text>
                {step.description && (
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>
                    {step.description}
                  </Text>
                )}
                {showTimestamps && step.timestamp && (
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>
                    {formatDate(step.timestamp)}
                    {step.actor && ` by ${step.actor}`}
                  </Text>
                )}
              </Box>
            </Group>
          );
        })}
      </Stack>
    );

    const renderBadges = () => (
      <Group gap="xs">
        {steps.map((step, index) => {
          const status = getStepStatus(index, step.key);
          const color = getStatusColor(status);

          return (
            <Tooltip key={step.key} label={step.description || step.label}>
              <Badge
                color={color}
                variant={status === 'current' ? 'filled' : status === 'completed' ? 'light' : 'outline'}
                leftSection={
                  status === 'completed' ? (
                    <IconCheck size={12} />
                  ) : status === 'failed' ? (
                    <IconX size={12} />
                  ) : status === 'current' ? (
                    <IconClock size={12} />
                  ) : undefined
                }
              >
                {step.label}
              </Badge>
            </Tooltip>
          );
        })}
      </Group>
    );

    return (
      <FadeIn>
        <Card ref={ref} className={className}>
          <Stack gap="md">
            {(title || subtitle) && (
              <Box>
                {title && <Text size="lg" fw={600} style={accessibilityStyles}>{title}</Text>}
                {subtitle && <Text size="sm" c="dimmed" style={accessibilityStyles}>{subtitle}</Text>}
              </Box>
            )}

            {variant === 'stepper' && renderStepper()}
            {variant === 'progress' && renderProgress()}
            {variant === 'timeline' && renderTimeline()}
            {variant === 'badges' && renderBadges()}
          </Stack>
        </Card>
      </FadeIn>
    );
  }
);

StatusTracker.displayName = 'StatusTracker';

export default StatusTracker;
